import React, { useState } from "react";
import { Mail, MapPin, Phone, Send, MessageCircle, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/lib/supabaseClient";
import Chatbot from "@/components/Chatbot";

const PHONE_NUMBER = "+252610437466";
const WHATSAPP_NUMBER = "252610437466";

export default function Contact() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<null | { ok: boolean; message: string }>(null);
    const [fabOpen, setFabOpen] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log("Submitting to Supabase...", { name, email, subject, message });
        // @ts-ignore
        console.log("Supabase Client Config:", { url: supabase.supabaseUrl });

        // @ts-ignore
        if (supabase.supabaseUrl === 'https://dummy.supabase.co') {
            setStatus({ ok: false, message: "Error: Missing Supabase Environment Variables. Please restart your dev server or add them to Vercel." });
            setLoading(false);
            return;
        }

        setLoading(true);
        setStatus(null);

        const payload = {
            name,
            email,
            subject,
            message,
        } as any;

        try {
            const { error } = await supabase.from("Send_Message_able").insert([payload]);
            if (error) throw error;
            setStatus({ ok: true, message: "Message sent successfully." });
            setName("");
            setEmail("");
            setSubject("");
            setMessage("");
        } catch (err: any) {
            setStatus({ ok: false, message: err.message || "Failed to send message." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" className="py-24 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                    <div>
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready To Start a Project?</h2>
                        <p className="text-muted-foreground text-lg mb-12 max-w-sm">
                            Let's discuss how we can work together to bring your vision to life.
                        </p>

                        <div className="space-y-6">
                            <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                                    <Mail className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground">Email</p>
                                    <a href="mailto:muscabdigale@gmail.com" className="text-lg font-bold hover:text-neon transition-colors">muscabdigale@gmail.com</a>
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                                    <MapPin className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground">Location</p>
                                    <p className="text-lg font-bold">Mogadisho  / Somalia</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-secondary/30 p-8 rounded-2xl border border-secondary shadow-sm">
                        <form className="space-y-6" onSubmit={handleSubmit}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" htmlFor="name">Name</label>
                                    <Input id="name" placeholder="Muscab Digale" value={name} onChange={(e) => setName(e.target.value)} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" htmlFor="email">Email</label>
                                    <Input id="email" placeholder="muscabdigale@gmail.com" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" htmlFor="subject">Subject</label>
                                <select
                                    id="subject"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <option value="" disabled>Select a subject</option>
                                    <option value="project">Project Inquiry</option>
                                    <option value="feedback">Feedback</option>
                                    <option value="advice">Advice / Consultation</option>
                                    <option value="collaboration">Collaboration</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" htmlFor="message">Message</label>
                                <Textarea id="message" placeholder="How can I help you?" className="min-h-[150px]" value={message} onChange={(e) => setMessage(e.target.value)} />
                            </div>

                            <Button type="submit" size="lg" className="w-full bg-neon text-black font-bold hover:bg-neon/90" disabled={loading}>
                                {loading ? "Sending..." : "SEND MESSAGE"}
                            </Button>

                            {status && (
                                <p className={`text-sm mt-2 ${status.ok ? 'text-green-500' : 'text-red-500'}`}>{status.message}</p>
                            )}
                        </form>


                    </div>
                </div>
            </div>

            {/* ══════════════ Speed-Dial FAB ══════════════ */}
            {fabOpen && (
                <div className="fixed inset-0 z-40" onClick={() => setFabOpen(false)} />
            )}

            <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">

                {/* 4 action buttons — only in DOM when open */}
                {fabOpen && (
                    <div className="flex flex-col items-end gap-3">

                        {/* Send Message */}
                        <div className="flex items-center gap-3 animate-fade-in">
                            <span className="bg-background border border-secondary text-sm font-medium px-3 py-1 rounded-lg shadow-lg whitespace-nowrap">
                                Send Message
                            </span>
                            <button
                                onClick={() => {
                                    setFabOpen(false);
                                    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                                    setTimeout(() => document.getElementById("name")?.focus(), 600);
                                }}
                                className="w-14 h-14 rounded-full bg-neon text-black shadow-lg hover:scale-110 transition-transform duration-200 flex items-center justify-center shrink-0"
                            >
                                <Send className="w-5 h-5" />
                            </button>
                        </div>

                        {/* WhatsApp */}
                        <div className="flex items-center gap-3 animate-fade-in" style={{ animationDelay: "60ms" }}>
                            <span className="bg-background border border-secondary text-sm font-medium px-3 py-1 rounded-lg shadow-lg whitespace-nowrap">
                                WhatsApp
                            </span>
                            <a
                                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setFabOpen(false)}
                                className="w-14 h-14 rounded-full bg-[#25D366] shadow-lg hover:scale-110 transition-transform duration-200 flex items-center justify-center shrink-0"
                            >
                                <svg viewBox="0 0 32 32" className="w-7 h-7 fill-white">
                                    <path d="M16 2C8.28 2 2 8.28 2 16c0 2.52.67 4.88 1.83 6.92L2 30l7.28-1.9A13.93 13.93 0 0016 30c7.72 0 14-6.28 14-14S23.72 2 16 2zm0 25.43c-2.3 0-4.45-.63-6.3-1.72l-.45-.27-4.32 1.13 1.15-4.2-.3-.46A11.44 11.44 0 014.57 16c0-6.31 5.12-11.43 11.43-11.43S27.43 9.69 27.43 16 22.31 27.43 16 27.43zm6.27-8.57c-.34-.17-2.02-1-2.34-1.1-.32-.12-.55-.17-.78.17-.23.34-.9 1.1-1.1 1.33-.2.23-.4.26-.74.09-.34-.17-1.44-.53-2.74-1.69-1.01-.9-1.7-2.01-1.9-2.35-.2-.34-.02-.52.15-.69.15-.15.34-.4.52-.6.17-.2.23-.34.34-.56.12-.23.06-.43-.03-.6-.09-.17-.78-1.88-1.07-2.57-.28-.67-.57-.58-.78-.59h-.66c-.23 0-.6.09-.91.43-.31.34-1.2 1.17-1.2 2.86 0 1.69 1.23 3.32 1.4 3.55.17.23 2.42 3.7 5.87 5.18.82.35 1.46.56 1.96.72.82.26 1.57.22 2.16.13.66-.1 2.02-.82 2.3-1.62.28-.8.28-1.48.2-1.62-.09-.14-.31-.23-.65-.4z" />
                                </svg>
                            </a>
                        </div>

                        {/* Call Me */}
                        <div className="flex items-center gap-3 animate-fade-in" style={{ animationDelay: "120ms" }}>
                            <span className="bg-background border border-secondary text-sm font-medium px-3 py-1 rounded-lg shadow-lg whitespace-nowrap">
                                Call Me
                            </span>
                            <a
                                href={`tel:${PHONE_NUMBER}`}
                                onClick={() => setFabOpen(false)}
                                className="w-14 h-14 rounded-full bg-secondary border border-secondary/60 shadow-lg hover:scale-110 hover:bg-blue-500/20 transition-transform duration-200 flex items-center justify-center shrink-0"
                            >
                                <Phone className="w-6 h-6 text-blue-400" />
                            </a>
                        </div>

                        {/* Chatbot */}
                        <div className="flex items-center gap-3 animate-fade-in" style={{ animationDelay: "180ms" }}>
                            <span className="bg-background border border-secondary text-sm font-medium px-3 py-1 rounded-lg shadow-lg whitespace-nowrap">
                                Chatbot
                            </span>
                            <button
                                onClick={() => { setFabOpen(false); setChatOpen(true); }}
                                className="w-14 h-14 rounded-full bg-secondary border border-secondary/60 shadow-lg hover:scale-110 hover:bg-neon/20 transition-transform duration-200 flex items-center justify-center shrink-0"
                            >
                                <Bot className="w-6 h-6 text-neon" />
                            </button>
                        </div>

                    </div>
                )}

                {/* Main toggle button — always visible, ONE button only */}
                <button
                    onClick={() => setFabOpen(o => !o)}
                    className="w-16 h-16 rounded-full bg-neon text-black shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
                    style={{ boxShadow: fabOpen ? "0 0 0 4px rgba(132,204,22,0.25)" : undefined }}
                >
                    <MessageCircle
                        className="w-7 h-7 transition-transform duration-300"
                        style={{ transform: fabOpen ? "rotate(45deg)" : "rotate(0deg)" }}
                    />
                </button>

            </div>

            {/* ══════════════ Chatbot ══════════════ */}
            <Chatbot isOpen={chatOpen} onClose={() => setChatOpen(false)} />
        </section>
    );
}
